import { STORAGE } from "@constants/Storage";
import { parseToken } from "@piiqtechnologies/ui-lib";
import { buildOrgPath, PATHS } from "@constants/Paths";
import { ALL_DISCOVER_ACTIONS, DEMO_ACTIONS, ORG_ADMIN_ACTIONS } from "@constants/Policies";
import { getStoredMemberships } from "@stores/memberships";
export const getUserContext = () => {
    let userContext = null;
    try {
        const userDetailsString = sessionStorage.getItem(STORAGE.USER_CONTEXT);
        userContext = (userDetailsString && JSON.parse(userDetailsString)) || null;
    }
    catch (_a) {
        console.log("Session Storage access issue!!!");
    }
    return userContext;
};
export const storeUserContext = (useContext) => {
    try {
        sessionStorage.setItem(STORAGE.USER_CONTEXT, JSON.stringify(useContext));
    }
    catch (_a) {
        console.log("Session Storage access issue!!!");
    }
};
export const getIsAdmin = (user) => (user === null || user === void 0 ? void 0 : user.roleAlias) === "admin";
const hasDiscoverRole = (user, alias) => {
    const { discoverRole, discoverRoleAlias } = user || {};
    if (discoverRoleAlias === alias)
        return true;
    if (typeof discoverRole === "string")
        return discoverRole === alias;
    return (discoverRole === null || discoverRole === void 0 ? void 0 : discoverRole.alias) === alias;
};
export const getIsDemoUser = (user) => hasDiscoverRole(user, "discover_demo");
export const getIsViewerUser = (user) => hasDiscoverRole(user, "discover_viewer");
export const getIsDiscoverAdmin = (user) => hasDiscoverRole(user, "discover_admin");
export const isAdminUser = (userContext) => {
    if (userContext) {
        return getIsAdmin(userContext === null || userContext === void 0 ? void 0 : userContext.user);
    }
    return false;
};
/**
 * Pure branching rule for where a signed-in user lands, given their staff status and known
 * memberships. Staff wins over membership count: staff accounts receive every organization, so a
 * staff user with many memberships still goes to the dashboard, never the picker.
 *
 * `memberships` distinguishes an answer from the absence of one: `[]` means the account was
 * confirmed to hold nothing, `null` means we never learned its organizations this session (the
 * bootstrap fetch failed). Collapsing the two would show the revoked-access screen to a user whose
 * request merely timed out, which is both wrong and unactionable.
 *
 * - staff -> the unprefixed admin dashboard.
 * - unknown memberships -> the organization picker, which owns resolving them (it retries the
 *   fetch and can offer the user a retry) rather than the dead-end restricted screen.
 * - 0 memberships -> the restricted screen, rather than the dead-end unprefixed `/home`.
 * - 1 membership -> straight to that org's home.
 * - 2+ memberships -> the organization picker for an explicit choice.
 */
export const resolvePostLoginPath = ({ isStaff, memberships, }) => {
    if (isStaff)
        return PATHS.ADMIN_DASHBOARD;
    if (memberships === null)
        return PATHS.SELECT_ORGANIZATION;
    if (memberships.length === 0)
        return PATHS.RESTRICTED;
    if (memberships.length === 1)
        return buildOrgPath(memberships[0]._id, PATHS.HOME);
    return PATHS.SELECT_ORGANIZATION;
};
/**
 * Where a signed-in user should land after login, reading memberships from session storage. Nothing
 * stored is passed through as `null` (unknown), not flattened to `[]` — a successful fetch always
 * writes a list, so "no key" and "empty list" are genuinely different facts.
 */
export const getRedirectPathAfterLogin = (user) => resolvePostLoginPath({ isStaff: getIsAdmin(user), memberships: getStoredMemberships() });
/**
 * Pulls the first organization id off a freshly-issued JWT, mirroring AuthContext's
 * normalizeUserFromToken claim extraction (organizations, then discoverOrganizations; each
 * entry may be an object or a bare id string). Lets a caller that only has a raw signup/login
 * token — not yet a stored User — org-scope its own post-auth navigation without waiting for a
 * membership fetch.
 */
export const getFirstOrgIdFromToken = (token) => {
    var _a, _b, _c;
    try {
        const payload = parseToken(token);
        const first = (_b = (_a = payload === null || payload === void 0 ? void 0 : payload.organizations) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : (_c = payload === null || payload === void 0 ? void 0 : payload.discoverOrganizations) === null || _c === void 0 ? void 0 : _c[0];
        if (!first)
            return undefined;
        return typeof first === "string" ? first : first._id;
    }
    catch (_d) {
        return undefined;
    }
};
/**
 * Capabilities are per-organization, not per-account. The same person can be `discover_admin` in
 * one organization and `discover_viewer` in another, but the JWT carries a single account-level
 * discover role — so gating on the token alone handed org-admin capabilities (Account Settings,
 * billing, assessment authoring) to a viewer the moment they opened an organization where they
 * only read.
 *
 * `orgRole` is therefore authoritative whenever it is known: inside `/o/:orgId` the resolved
 * membership's own role decides, and anything that is not `discover_admin` gets nothing.
 *
 * Staff and demo accounts are resolved before it: staff are not scoped to an organization at all
 * (their memberships carry `role: null`), and the demo sandbox is an account-level persona.
 *
 * With no `orgRole` the account-level role still decides. That is the unprefixed staff-canonical
 * routes and the pre-resolution window, where withholding everything would blank the sidebar for a
 * legitimate org admin rather than protect anything — `ActiveOrgLayout` supplies the real role
 * before any org-scoped page renders.
 */
export const generateAllowedActions = ({ user, orgRole }) => {
    if (getIsAdmin(user))
        return [...ALL_DISCOVER_ACTIONS];
    if (getIsDemoUser(user))
        return [...DEMO_ACTIONS];
    if (orgRole)
        return orgRole === "discover_admin" ? [...ORG_ADMIN_ACTIONS] : [];
    if (getIsDiscoverAdmin(user))
        return [...ORG_ADMIN_ACTIONS];
    return [];
};
